// HousekeepingDashboard.js — Housekeeping Operations Center (Hotel Desk PMS)
// Enterprise redesign. 100% dynamic: bound to the existing housekeeping APIs
// plus the read-only /housekeeping/room-layout enrichment endpoint. No dummy
// data, no static counts — everything is derived from live responses.
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Container, Card, Typography, Grid, Chip, Snackbar, Alert, Button,
  IconButton, Drawer, Divider, TextField, MenuItem, InputAdornment, Tooltip,
  CircularProgress, ToggleButtonGroup, ToggleButton, Avatar, Badge, Stack,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import { housekeepingApi, ctx } from './modulesApi';

/* ── Hotel Desk theme tokens ─────────────────────────────────────────── */
const T = {
  red: '#C8102E', redD: '#9E0C22', navy: '#1A2238', bg: '#F8FAFC',
  card: '#FFFFFF', text: '#0F172A', dim: '#64748B', border: '#E7ECF3',
  radius: 14,
  shadow: '0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)',
  shadowH: '0 10px 28px rgba(15,23,42,0.12)',
};

/* Material Symbols Rounded — same convention as AdminLayout */
const MSym = ({ name, size = 20, color, fill = 0, style }) => (
  <span
    className='material-symbols-rounded'
    style={{
      fontSize: size, color, lineHeight: 1, userSelect: 'none',
      fontVariationSettings: `'FILL' ${fill},'wght' 500,'GRAD' 0,'opsz' 24`,
      ...style,
    }}>
    {name}
  </span>
);

/* Effective room status → visual style */
const STATUS = {
  Occupied:    { label: 'Occupied',      color: '#2563EB', icon: 'bed' },
  VacantClean: { label: 'Vacant Clean',  color: '#16A34A', icon: 'check_circle' },
  VacantDirty: { label: 'Vacant Dirty',  color: '#F59E0B', icon: 'cleaning_services' },
  Cleaning:    { label: 'Cleaning',      color: '#EAB308', icon: 'mop' },
  Inspection:  { label: 'Inspection',    color: '#7C3AED', icon: 'search' },
  Ready:       { label: 'Ready',         color: '#0D9488', icon: 'task_alt' },
  Blocked:     { label: 'Blocked',       color: '#6B7280', icon: 'block' },
  OutOfOrder:  { label: 'Out of Order',  color: '#DC2626', icon: 'dangerous' },
  Maintenance: { label: 'Maintenance',   color: '#EA580C', icon: 'handyman' },
  Vacant:      { label: 'Vacant',        color: '#94A3B8', icon: 'door_open' },
};

function effStatus(r) {
  const hk = r.housekeepingStatus;
  if (r.hasMaintenance) return 'Maintenance';
  if (hk === 'UnderMaintenance') return 'OutOfOrder';
  if (hk === 'Blocked') return 'Blocked';
  if (hk === 'InspectionPending') return 'Inspection';
  if (r.taskStatus === 'InProgress') return 'Cleaning';
  if (hk === 'Dirty') return 'VacantDirty';
  if (hk === 'Ready') return 'Ready';
  if (r.occupancy === 'Occupied') return 'Occupied';
  if (hk === 'Clean') return 'VacantClean';
  return 'Vacant';
}

const PRIORITY_COLOR = { Urgent: '#DC2626', High: '#EA580C', Normal: '#2563EB', Low: '#64748B' };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—');
const isToday = (d) => d && new Date(d).toDateString() === new Date().toDateString();
const TABS = ['Room Layout', 'Cleaning Board', 'Staff Workload', 'Maintenance', 'Analytics'];
const KANBAN_COLS = [
  { key: 'Assigned', label: 'Assigned', color: '#2563EB' },
  { key: 'InProgress', label: 'Cleaning', color: '#EAB308' },
  { key: 'Completed', label: 'Completed', color: '#0D9488' },
  { key: 'Verified', label: 'Verified', color: '#16A34A' },
];

/* ── Small presentational pieces ─────────────────────────────────────── */
const Panel = ({ children, sx }) => (
  <Card elevation={0} sx={{ borderRadius: `${T.radius}px`, border: `1px solid ${T.border}`, boxShadow: T.shadow, background: T.card, ...sx }}>
    {children}
  </Card>
);

const KpiCard = ({ k, active, onClick }) => (
  <Card
    elevation={0}
    onClick={onClick}
    sx={{
      cursor: 'pointer', borderRadius: `${T.radius}px`, p: 1.6,
      border: `1px solid ${active ? k.color : T.border}`,
      boxShadow: active ? `0 0 0 2px ${k.color}22` : T.shadow,
      background: T.card, transition: 'all .15s',
      '&:hover': { boxShadow: T.shadowH, transform: 'translateY(-2px)' },
    }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box
        sx={{ width: 34, height: 34, borderRadius: '10px', display: 'grid', placeItems: 'center', background: `${k.color}14` }}>
        <MSym name={k.icon} size={20} color={k.color} fill={1} />
      </Box>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: T.text, lineHeight: 1 }}>{k.count}</Typography>
    </Box>
    <Typography sx={{ mt: 1, fontSize: 12, fontWeight: 600, color: T.dim }}>{k.label}</Typography>
  </Card>
);

/* ── Main component ──────────────────────────────────────────────────── */
export default function HousekeepingDashboard() {
  const { companyId, hotelId, userId } = ctx();

  const [layout, setLayout] = useState({ rooms: [], floors: [] });
  const [dash, setDash] = useState({ roomStatusCounts: [], activeTasks: [], delayedTasks: [], staffWorkload: [] });
  const [maint, setMaint] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [perf, setPerf] = useState([]);

  const [loading, setLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [auto, setAuto] = useState(true);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const [tab, setTab] = useState(0);
  const [floor, setFloor] = useState('all');
  const [layoutMode, setLayoutMode] = useState('grid');
  const [kpiFilter, setKpiFilter] = useState(null);
  const [flt, setFlt] = useState({ room: '', guest: '', type: '', hk: '', staff: '', priority: '' });
  const setF = (k, v) => setFlt((p) => ({ ...p, [k]: v }));

  const [drawerRoom, setDrawerRoom] = useState(null);
  const [assignStaffId, setAssignStaffId] = useState('');

  const rooms = layout.rooms || [];

  const loadAll = useCallback(async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const [rl, db, mt, tk, st] = await Promise.all([
        housekeepingApi.roomLayout(companyId, hotelId),
        housekeepingApi.dashboard(companyId, hotelId),
        housekeepingApi.listMaintenance(hotelId),
        housekeepingApi.listTasks(hotelId, { pageSize: 200 }),
        housekeepingApi.listStaff(hotelId, { pageSize: 200 }),
      ]);
      setLayout(rl.data || { rooms: [], floors: [] });
      setDash(db.data || {});
      setMaint(mt.data || []);
      setTasks(tk.data?.items || []);
      setStaff(st.data?.items || []);
      setLastSync(new Date());
    } catch {
      notify('Failed to load housekeeping data', 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, hotelId]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    if (!auto) return undefined;
    const t = setInterval(loadAll, 60000);
    return () => clearInterval(t);
  }, [auto, loadAll]);

  // Analytics loads on demand (last 30 days)
  useEffect(() => {
    if (tab !== 4 || !hotelId) return;
    const to = new Date();
    const from = new Date(); from.setDate(from.getDate() - 30);
    housekeepingApi
      .staffPerformance(hotelId, from.toISOString().slice(0, 10), to.toISOString().slice(0, 10))
      .then((r) => setPerf(r.data || []))
      .catch(() => setPerf([]));
  }, [tab, hotelId]);

  /* KPI cards — derived live from rooms + dashboard */
  const kpis = useMemo(() => {
    const by = (s) => rooms.filter((r) => effStatus(r) === s).length;
    return [
      { key: 'Occupied', label: 'Occupied', icon: 'bed', color: STATUS.Occupied.color, count: by('Occupied') },
      { key: 'VacantClean', label: 'Vacant Clean', icon: 'check_circle', color: STATUS.VacantClean.color, count: by('VacantClean') },
      { key: 'VacantDirty', label: 'Vacant Dirty', icon: 'cleaning_services', color: STATUS.VacantDirty.color, count: by('VacantDirty') },
      { key: 'Cleaning', label: 'Cleaning In Progress', icon: 'mop', color: STATUS.Cleaning.color, count: by('Cleaning') },
      { key: 'Inspection', label: 'Inspection Pending', icon: 'search', color: STATUS.Inspection.color, count: by('Inspection') },
      { key: 'Ready', label: 'Ready Rooms', icon: 'task_alt', color: STATUS.Ready.color, count: by('Ready') },
      { key: 'OutOfOrder', label: 'Out Of Order', icon: 'dangerous', color: STATUS.OutOfOrder.color, count: by('OutOfOrder') },
      { key: 'Maintenance', label: 'Maintenance', icon: 'handyman', color: STATUS.Maintenance.color, count: by('Maintenance') },
      { key: 'Arrivals', label: "Today's Arrivals", icon: 'flight_land', color: '#0EA5E9', count: rooms.filter((r) => isToday(r.checkIn)).length, tab: null },
      { key: 'Departures', label: "Today's Departures", icon: 'flight_takeoff', color: '#8B5CF6', count: rooms.filter((r) => isToday(r.checkOut)).length, tab: null },
      { key: 'OpenTasks', label: 'Open Tasks', icon: 'assignment', color: T.red, count: (dash.activeTasks || []).length, tab: 1 },
      { key: 'Delayed', label: 'Delayed Tasks', icon: 'schedule', color: '#DC2626', count: (dash.delayedTasks || []).length, tab: 1 },
    ];
  }, [rooms, dash]);

  const onKpiClick = (k) => {
    if (k.tab === 1) { setTab(1); return; }
    if (k.key === 'Arrivals' || k.key === 'Departures') { setTab(0); setKpiFilter(k.key); return; }
    setTab(0);
    setKpiFilter((p) => (p === k.key ? null : k.key));
  };

  const roomTypes = useMemo(() => [...new Set(rooms.map((r) => r.roomType).filter(Boolean))], [rooms]);
  const staffNames = useMemo(() => [...new Set(rooms.map((r) => r.assignedStaffName).filter(Boolean))], [rooms]);

  const filteredRooms = useMemo(() => rooms.filter((r) => {
    if (floor !== 'all' && r.floor !== floor) return false;
    if (kpiFilter === 'Arrivals' && !isToday(r.checkIn)) return false;
    else if (kpiFilter === 'Departures' && !isToday(r.checkOut)) return false;
    else if (kpiFilter && !['Arrivals', 'Departures'].includes(kpiFilter) && effStatus(r) !== kpiFilter) return false;
    if (flt.room && !String(r.roomNumber).includes(flt.room)) return false;
    if (flt.guest && !(r.guestName || '').toLowerCase().includes(flt.guest.toLowerCase())) return false;
    if (flt.type && r.roomType !== flt.type) return false;
    if (flt.hk && effStatus(r) !== flt.hk) return false;
    if (flt.staff && r.assignedStaffName !== flt.staff) return false;
    if (flt.priority && r.cleaningPriority !== flt.priority) return false;
    return true;
  }), [rooms, floor, kpiFilter, flt]);

  /* ── Actions (all hit existing endpoints) ── */
  const afterAction = async (msg) => { notify(msg); await loadAll(); };

  const setRoomStatus = async (room, status, label) => {
    try {
      await housekeepingApi.setRoomStatus(hotelId, room.roomId, status, null, userId);
      setDrawerRoom((d) => (d ? { ...d, housekeepingStatus: status } : d));
      await afterAction(`Room ${room.roomNumber} → ${label}`);
    } catch { notify('Action failed', 'error'); }
  };
  const changeTask = async (taskId, toStatus) => {
    try {
      await housekeepingApi.changeTaskStatus({ taskId, toStatus, changedBy: userId });
      await afterAction(`Task marked ${toStatus}`);
    } catch (e) { notify(e?.response?.data?.error || 'Invalid transition', 'error'); }
  };
  const assignCleaning = async (room) => {
    if (!assignStaffId) { notify('Select a staff member', 'warning'); return; }
    try {
      await housekeepingApi.createTask({
        companyId, hotelId, roomId: room.roomId, roomNumber: String(room.roomNumber),
        staffId: Number(assignStaffId), taskType: 'FullCleaning', priority: 'Normal', createdBy: userId,
      });
      setAssignStaffId('');
      await afterAction(`Cleaning assigned for room ${room.roomNumber}`);
    } catch { notify('Assign failed', 'error'); }
  };
  const reportMaintenance = async (room) => {
    const issue = window.prompt(`Describe the maintenance issue for room ${room.roomNumber}:`);
    if (!issue) return;
    try {
      await housekeepingApi.raiseMaintenance(
        { companyId, hotelId, roomId: room.roomId, issueDescription: issue, priority: 'Normal', status: 'Open' },
        userId,
      );
      await afterAction('Maintenance reported');
    } catch { notify('Failed to report maintenance', 'error'); }
  };
  const resolveMaintenance = async (m) => {
    try {
      await housekeepingApi.updateMaintenance({ ...m, status: 'Resolved' }, userId);
      await afterAction('Maintenance resolved');
    } catch { notify('Update failed', 'error'); }
  };

  if (loading && rooms.length === 0) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center', background: T.bg }}>
        <CircularProgress sx={{ color: T.red }} />
      </Box>
    );
  }

  const activeTask = drawerRoom && tasks.find((t) => t.roomId === drawerRoom.roomId && ['Assigned', 'InProgress'].includes(t.status));

  return (
    <Box sx={{ background: T.bg, minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
      <Container maxWidth='xl' sx={{ py: 3 }}>

        {/* ── HEADER ── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 1.4, alignItems: 'center' }}>
            <Box sx={{ width: 46, height: 46, borderRadius: '12px', display: 'grid', placeItems: 'center', background: `linear-gradient(135deg,${T.red},${T.redD})` }}>
              <MSym name='cleaning_services' size={26} color='#fff' fill={1} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: T.navy, lineHeight: 1.15 }}>Housekeeping Dashboard</Typography>
              <Typography sx={{ fontSize: 13, color: T.dim }}>
                Manage room cleaning, inspections, maintenance and room readiness in real time.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size='small'
              onClick={() => setAuto((a) => !a)}
              icon={<MSym name={auto ? 'sync' : 'sync_disabled'} size={16} color={auto ? '#16A34A' : T.dim} />}
              label={auto ? 'Auto-refresh On' : 'Auto-refresh Off'}
              sx={{ fontWeight: 600, bgcolor: auto ? '#16A34A14' : '#00000008', color: auto ? '#16A34A' : T.dim, cursor: 'pointer' }}
            />
            <Typography sx={{ fontSize: 12, color: T.dim, display: { xs: 'none', sm: 'block' } }}>
              Last sync {lastSync ? lastSync.toLocaleTimeString('en-GB') : '—'}
            </Typography>
            <Tooltip title='Refresh'>
              <IconButton onClick={loadAll} sx={{ border: `1px solid ${T.border}`, borderRadius: '10px' }}>
                <MSym name='refresh' size={20} color={T.navy} />
              </IconButton>
            </Tooltip>
            <Tooltip title='Delayed tasks'>
              <IconButton onClick={() => setTab(1)} sx={{ border: `1px solid ${T.border}`, borderRadius: '10px' }}>
                <Badge badgeContent={(dash.delayedTasks || []).length} color='error'>
                  <MSym name='notifications' size={20} color={T.navy} />
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* ── KPI CARDS ── */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          {kpis.map((k) => (
            <Grid item xs={6} sm={4} md={2} key={k.key}>
              <KpiCard k={k} active={kpiFilter === k.key} onClick={() => onKpiClick(k)} />
            </Grid>
          ))}
        </Grid>

        {/* ── TABS ── */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
          {TABS.map((t, i) => (
            <Button
              key={t}
              onClick={() => setTab(i)}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: 13.5, borderRadius: '10px', px: 2, py: 0.8,
                color: tab === i ? '#fff' : T.navy,
                background: tab === i ? `linear-gradient(135deg,${T.red},${T.redD})` : 'transparent',
                border: `1px solid ${tab === i ? 'transparent' : T.border}`,
                '&:hover': { background: tab === i ? `linear-gradient(135deg,${T.red},${T.redD})` : '#00000006' },
              }}>
              {t}
            </Button>
          ))}
        </Box>

        {/* ── TAB 0: ROOM LAYOUT ── */}
        {tab === 0 && (
          <>
            {/* Floor filters */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FloorChip label='All Floors' count={rooms.length} active={floor === 'all'} onClick={() => setFloor('all')} />
              {(layout.floors || []).map((f) => (
                <FloorChip key={f.floor} label={`Floor ${f.floor}`} count={f.roomCount} active={floor === f.floor} onClick={() => setFloor(f.floor)} />
              ))}
              <Box sx={{ flex: 1 }} />
              <ToggleButtonGroup size='small' exclusive value={layoutMode} onChange={(e, v) => v && setLayoutMode(v)}>
                <ToggleButton value='grid'><MSym name='grid_view' size={18} /></ToggleButton>
                <ToggleButton value='list'><MSym name='view_list' size={18} /></ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Filter bar */}
            <Panel sx={{ p: 1.5, mb: 2 }}>
              <Grid container spacing={1.2}>
                <FilterField icon='search' ph='Search Room' value={flt.room} onChange={(v) => setF('room', v)} />
                <FilterField icon='person_search' ph='Search Guest' value={flt.guest} onChange={(v) => setF('guest', v)} />
                <FilterSelect ph='Room Type' value={flt.type} onChange={(v) => setF('type', v)} options={roomTypes} />
                <FilterSelect ph='HK Status' value={flt.hk} onChange={(v) => setF('hk', v)} options={Object.keys(STATUS)} labels={Object.fromEntries(Object.entries(STATUS).map(([k, v]) => [k, v.label]))} />
                <FilterSelect ph='Assigned Staff' value={flt.staff} onChange={(v) => setF('staff', v)} options={staffNames} />
                <FilterSelect ph='Priority' value={flt.priority} onChange={(v) => setF('priority', v)} options={['Urgent', 'High', 'Normal', 'Low']} />
              </Grid>
              {(kpiFilter || Object.values(flt).some(Boolean)) && (
                <Button size='small' onClick={() => { setKpiFilter(null); setFlt({ room: '', guest: '', type: '', hk: '', staff: '', priority: '' }); }}
                  sx={{ mt: 1, textTransform: 'none', color: T.red }} startIcon={<MSym name='filter_alt_off' size={16} color={T.red} />}>
                  Clear filters
                </Button>
              )}
            </Panel>

            {/* Room grid / list */}
            {filteredRooms.length === 0 ? (
              <Panel sx={{ p: 6, textAlign: 'center' }}>
                <MSym name='meeting_room' size={40} color={T.dim} />
                <Typography sx={{ color: T.dim, mt: 1 }}>No rooms match the current filters.</Typography>
              </Panel>
            ) : layoutMode === 'grid' ? (
              <Grid container spacing={1.5}>
                {filteredRooms.map((r) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={r.roomId}>
                    <RoomCard r={r} onClick={() => { setDrawerRoom(r); setAssignStaffId(''); }} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Panel>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, color: T.dim, bgcolor: '#F8FAFC' } }}>
                        <TableCell>Room</TableCell><TableCell>Type</TableCell><TableCell>Floor</TableCell>
                        <TableCell>Status</TableCell><TableCell>Guest</TableCell><TableCell>Staff</TableCell><TableCell>Priority</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRooms.map((r) => {
                        const s = STATUS[effStatus(r)];
                        return (
                          <TableRow key={r.roomId} hover sx={{ cursor: 'pointer' }} onClick={() => { setDrawerRoom(r); setAssignStaffId(''); }}>
                            <TableCell sx={{ fontWeight: 700 }}>{r.roomNumber}</TableCell>
                            <TableCell>{r.roomType || '—'}</TableCell>
                            <TableCell>{r.floor}</TableCell>
                            <TableCell><Chip size='small' label={s.label} sx={{ bgcolor: `${s.color}18`, color: s.color, fontWeight: 700 }} /></TableCell>
                            <TableCell>{r.guestName || '—'}</TableCell>
                            <TableCell>{r.assignedStaffName || '—'}</TableCell>
                            <TableCell>{r.cleaningPriority || '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Panel>
            )}
          </>
        )}

        {/* ── TAB 1: CLEANING BOARD (Kanban, native drag & drop) ── */}
        {tab === 1 && (
          <Grid container spacing={1.5}>
            {KANBAN_COLS.map((col) => {
              const items = tasks.filter((t) => t.status === col.key);
              return (
                <Grid item xs={12} sm={6} md={3} key={col.key}>
                  <Panel
                    sx={{ p: 1.2, minHeight: 300 }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      const id = Number(e.dataTransfer.getData('taskId'));
                      const from = e.dataTransfer.getData('from');
                      if (id && from !== col.key) changeTask(id, col.key);
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: col.color }} />
                      <Typography sx={{ fontWeight: 800, color: T.navy, fontSize: 14 }}>{col.label}</Typography>
                      <Chip size='small' label={items.length} sx={{ ml: 'auto', height: 20, fontWeight: 700 }} />
                    </Box>
                    <Stack spacing={1}>
                      {items.map((t) => (
                        <Card
                          key={t.taskId}
                          elevation={0}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.setData('taskId', t.taskId); e.dataTransfer.setData('from', t.status); }}
                          sx={{ p: 1.2, borderRadius: '10px', border: `1px solid ${T.border}`, borderLeft: `3px solid ${col.color}`, cursor: 'grab', '&:hover': { boxShadow: T.shadow } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontWeight: 800, color: T.navy }}>Room {t.roomNumber}</Typography>
                            <Chip size='small' label={t.priority} sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: `${PRIORITY_COLOR[t.priority] || T.dim}18`, color: PRIORITY_COLOR[t.priority] || T.dim }} />
                          </Box>
                          <Typography sx={{ fontSize: 12, color: T.dim }}>{t.taskType}</Typography>
                          {t.staffName && <Typography sx={{ fontSize: 11.5, color: T.dim, mt: 0.3 }}>👤 {t.staffName}</Typography>}
                        </Card>
                      ))}
                      {items.length === 0 && <Typography sx={{ fontSize: 12, color: T.dim, textAlign: 'center', py: 2 }}>Drop tasks here</Typography>}
                    </Stack>
                  </Panel>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* ── TAB 2: STAFF WORKLOAD ── */}
        {tab === 2 && (
          <Grid container spacing={1.5}>
            {(dash.staffWorkload || []).length === 0 && (
              <Grid item xs={12}><Panel sx={{ p: 4, textAlign: 'center', color: T.dim }}>No staff workload data.</Panel></Grid>
            )}
            {(dash.staffWorkload || []).map((s) => {
              const info = staff.find((x) => x.staffId === s.staffId) || {};
              return (
                <Grid item xs={12} sm={6} md={4} key={s.staffId}>
                  <Panel sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.5 }}>
                      <Avatar sx={{ bgcolor: T.navy, width: 40, height: 40, fontWeight: 700 }}>{(s.staffName || '?').charAt(0)}</Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 800, color: T.navy }}>{s.staffName}</Typography>
                        <Typography sx={{ fontSize: 12, color: T.dim }}>{s.shift || '—'} shift</Typography>
                      </Box>
                      <Chip size='small' label={info.availabilityStatus || 'Available'} sx={{ ml: 'auto', fontWeight: 700, bgcolor: info.availabilityStatus === 'Busy' ? '#F59E0B18' : info.availabilityStatus === 'OnLeave' ? '#DC262618' : '#16A34A18', color: info.availabilityStatus === 'Busy' ? '#B45309' : info.availabilityStatus === 'OnLeave' ? '#B91C1C' : '#15803D' }} />
                    </Box>
                    <Grid container spacing={1}>
                      <StatMini label='Open Tasks' value={s.openTasks} color={T.red} />
                      <StatMini label='Completed Today' value={s.completedToday} color='#16A34A' />
                    </Grid>
                  </Panel>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* ── TAB 3: MAINTENANCE ── */}
        {tab === 3 && (
          <Panel>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, color: T.dim, bgcolor: '#F8FAFC' } }}>
                    <TableCell>Room</TableCell><TableCell>Issue</TableCell><TableCell>Priority</TableCell>
                    <TableCell>Status</TableCell><TableCell>Assigned</TableCell><TableCell>Raised</TableCell><TableCell align='right'>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {maint.length === 0 && <TableRow><TableCell colSpan={7} align='center' sx={{ color: T.dim, py: 3 }}>No maintenance requests.</TableCell></TableRow>}
                  {maint.map((m) => (
                    <TableRow key={m.requestId} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{m.roomNumber || m.roomId || '—'}</TableCell>
                      <TableCell>{m.issueDescription}</TableCell>
                      <TableCell><Chip size='small' label={m.priority} sx={{ bgcolor: `${PRIORITY_COLOR[m.priority] || T.dim}18`, color: PRIORITY_COLOR[m.priority] || T.dim, fontWeight: 700 }} /></TableCell>
                      <TableCell><Chip size='small' label={m.status} color={m.status === 'Resolved' ? 'success' : m.status === 'Open' ? 'error' : 'warning'} /></TableCell>
                      <TableCell>{m.assignedStaffName || '—'}</TableCell>
                      <TableCell>{fmtDate(m.raisedAt)}</TableCell>
                      <TableCell align='right'>
                        {m.status !== 'Resolved' && (
                          <Button size='small' onClick={() => resolveMaintenance(m)} sx={{ textTransform: 'none', color: '#16A34A', fontWeight: 700 }}>Resolve</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Panel>
        )}

        {/* ── TAB 4: ANALYTICS ── */}
        {tab === 4 && (
          <>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <AnalyticCard icon='timer' color='#2563EB' label='Avg Cleaning Time'
                value={perf.length ? `${Math.round(perf.reduce((a, p) => a + (p.avgDurationMinutes || 0), 0) / perf.length)} min` : '—'} />
              <AnalyticCard icon='task_alt' color='#16A34A' label='Tasks Completed (30d)'
                value={perf.reduce((a, p) => a + (p.completed || 0), 0)} />
              <AnalyticCard icon='bolt' color='#EA580C' label='Fastest Staff'
                value={perf.length ? [...perf].filter((p) => p.avgDurationMinutes).sort((a, b) => a.avgDurationMinutes - b.avgDurationMinutes)[0]?.staffName || '—' : '—'} />
              <AnalyticCard icon='schedule' color='#DC2626' label='Delayed Tasks' value={(dash.delayedTasks || []).length} />
            </Grid>
            <Panel>
              <Typography sx={{ px: 2, pt: 2, fontWeight: 800, color: T.navy }}>Staff Productivity (last 30 days)</Typography>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, color: T.dim, bgcolor: '#F8FAFC' } }}>
                      <TableCell>Staff</TableCell><TableCell align='right'>Total</TableCell>
                      <TableCell align='right'>Completed</TableCell><TableCell align='right'>Avg Time</TableCell><TableCell align='right'>Productivity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {perf.length === 0 && <TableRow><TableCell colSpan={5} align='center' sx={{ color: T.dim, py: 3 }}>No performance data.</TableCell></TableRow>}
                    {perf.map((p) => (
                      <TableRow key={p.staffId} hover>
                        <TableCell sx={{ fontWeight: 700 }}>{p.staffName}</TableCell>
                        <TableCell align='right'>{p.total}</TableCell>
                        <TableCell align='right'>{p.completed}</TableCell>
                        <TableCell align='right'>{p.avgDurationMinutes ? `${Math.round(p.avgDurationMinutes)}m` : '—'}</TableCell>
                        <TableCell align='right'>{p.total ? `${Math.round((p.completed / p.total) * 100)}%` : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Panel>
          </>
        )}
      </Container>

      {/* ── ROOM DRAWER ── */}
      <Drawer anchor='right' open={!!drawerRoom} onClose={() => setDrawerRoom(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, background: T.bg } }}>
        {drawerRoom && (() => {
          const s = STATUS[effStatus(drawerRoom)];
          return (
            <Box>
              <Box sx={{ p: 2.2, background: `linear-gradient(135deg,${T.navy},#2A3654)`, color: '#fff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1 }}>Room {drawerRoom.roomNumber}</Typography>
                    <Typography sx={{ fontSize: 13, opacity: 0.8 }}>{drawerRoom.roomType || '—'} · Floor {drawerRoom.floor}</Typography>
                  </Box>
                  <IconButton size='small' onClick={() => setDrawerRoom(null)} sx={{ color: '#fff' }}><MSym name='close' size={20} color='#fff' /></IconButton>
                </Box>
                <Chip size='small' label={s.label} sx={{ mt: 1.2, bgcolor: '#ffffff22', color: '#fff', fontWeight: 700 }} icon={<MSym name={s.icon} size={15} color='#fff' fill={1} />} />
              </Box>

              <Box sx={{ p: 2 }}>
                <DrawerRow label='Occupancy' value={drawerRoom.occupancy} />
                {drawerRoom.guestName && <DrawerRow label='Guest' value={drawerRoom.guestName} />}
                {drawerRoom.checkIn && <DrawerRow label='Check-in' value={fmtDate(drawerRoom.checkIn)} />}
                {drawerRoom.checkOut && <DrawerRow label='Check-out' value={fmtDate(drawerRoom.checkOut)} />}
                <DrawerRow label='Housekeeping' value={drawerRoom.housekeepingStatus || '—'} />
                <DrawerRow label='Assigned Staff' value={drawerRoom.assignedStaffName || '—'} />
                <DrawerRow label='Priority' value={drawerRoom.cleaningPriority || '—'} />
                {drawerRoom.hasMaintenance && <DrawerRow label='Maintenance' value='Open request' color='#DC2626' />}
                {drawerRoom.statusUpdatedAt && <DrawerRow label='Updated' value={new Date(drawerRoom.statusUpdatedAt).toLocaleString('en-GB')} />}

                <Divider sx={{ my: 2 }} />

                {/* Assign cleaning */}
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.dim, mb: 0.8 }}>ASSIGN STAFF</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField select size='small' fullWidth value={assignStaffId} onChange={(e) => setAssignStaffId(e.target.value)} placeholder='Staff'>
                    <MenuItem value=''>Select staff…</MenuItem>
                    {staff.map((x) => <MenuItem key={x.staffId} value={x.staffId}>{x.staffName} ({x.shift})</MenuItem>)}
                  </TextField>
                  <Button variant='contained' onClick={() => assignCleaning(drawerRoom)} sx={{ bgcolor: T.navy, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#0F1626' } }}>Assign</Button>
                </Box>

                {/* Task actions */}
                {activeTask && (
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    {activeTask.status === 'Assigned' && <ActBtn icon='play_arrow' label='Start Cleaning' color='#EAB308' onClick={() => changeTask(activeTask.taskId, 'InProgress')} />}
                    {activeTask.status === 'InProgress' && <ActBtn icon='done_all' label='Complete Cleaning' color='#0D9488' onClick={() => changeTask(activeTask.taskId, 'Completed')} />}
                  </Box>
                )}

                {/* Room status actions */}
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.dim, mb: 0.8 }}>ROOM STATUS</Typography>
                <Grid container spacing={1}>
                  <ActGrid icon='search' label='Send For Inspection' color='#7C3AED' onClick={() => setRoomStatus(drawerRoom, 'InspectionPending', 'Inspection')} />
                  <ActGrid icon='task_alt' label='Mark Ready' color='#0D9488' onClick={() => setRoomStatus(drawerRoom, 'Ready', 'Ready')} />
                  <ActGrid icon='check_circle' label='Mark Clean' color='#16A34A' onClick={() => setRoomStatus(drawerRoom, 'Clean', 'Clean')} />
                  <ActGrid icon='cleaning_services' label='Mark Dirty' color='#F59E0B' onClick={() => setRoomStatus(drawerRoom, 'Dirty', 'Dirty')} />
                  <ActGrid icon='dangerous' label='Out of Order' color='#DC2626' onClick={() => setRoomStatus(drawerRoom, 'UnderMaintenance', 'Out of Order')} />
                  <ActGrid icon='handyman' label='Report Maintenance' color='#EA580C' onClick={() => reportMaintenance(drawerRoom)} />
                </Grid>
              </Box>
            </Box>
          );
        })()}
      </Drawer>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev} variant='filled'>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}

/* ── Sub-components ─────────────────────────────────────────────────── */
const FloorChip = ({ label, count, active, onClick }) => (
  <Box onClick={onClick}
    sx={{ px: 1.6, py: 0.7, borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.8,
      fontWeight: 700, fontSize: 13, transition: 'all .15s',
      color: active ? '#fff' : T.navy, background: active ? T.navy : '#fff', border: `1px solid ${active ? T.navy : T.border}` }}>
    {label}
    <Box component='span' sx={{ px: 0.7, borderRadius: '6px', fontSize: 11, background: active ? '#ffffff2a' : '#00000008' }}>{count}</Box>
  </Box>
);

const FilterField = ({ icon, ph, value, onChange }) => (
  <Grid item xs={12} sm={6} md={2}>
    <TextField size='small' fullWidth placeholder={ph} value={value} onChange={(e) => onChange(e.target.value)}
      InputProps={{ startAdornment: <InputAdornment position='start'><MSym name={icon} size={18} color={T.dim} /></InputAdornment> }} />
  </Grid>
);

const FilterSelect = ({ ph, value, onChange, options, labels }) => (
  <Grid item xs={12} sm={6} md={2}>
    <TextField select size='small' fullWidth value={value} onChange={(e) => onChange(e.target.value)}
      SelectProps={{ displayEmpty: true }}>
      <MenuItem value=''>{ph}</MenuItem>
      {options.map((o) => <MenuItem key={o} value={o}>{labels ? labels[o] : o}</MenuItem>)}
    </TextField>
  </Grid>
);

const RoomCard = ({ r, onClick }) => {
  const key = effStatus(r);
  const s = STATUS[key];
  return (
    <Card elevation={0} onClick={onClick}
      sx={{ cursor: 'pointer', borderRadius: `${T.radius}px`, overflow: 'hidden', border: `1px solid ${T.border}`,
        borderTop: `3px solid ${s.color}`, boxShadow: T.shadow, transition: 'all .15s',
        '&:hover': { boxShadow: T.shadowH, transform: 'translateY(-2px)' } }}>
      <Box sx={{ p: 1.3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography sx={{ fontSize: 19, fontWeight: 800, color: T.navy, lineHeight: 1 }}>{r.roomNumber}</Typography>
          <MSym name={s.icon} size={18} color={s.color} fill={1} />
        </Box>
        <Typography sx={{ fontSize: 11.5, color: T.dim, mt: 0.2 }} noWrap>{r.roomType || '—'}</Typography>
        <Chip size='small' label={s.label} sx={{ mt: 0.8, height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: `${s.color}16`, color: s.color }} />
        {r.guestName && <Typography sx={{ fontSize: 11.5, color: T.text, mt: 0.7 }} noWrap>👤 {r.guestName}</Typography>}
        {r.assignedStaffName && <Typography sx={{ fontSize: 11, color: T.dim, mt: 0.2 }} noWrap>🧹 {r.assignedStaffName}</Typography>}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.7, flexWrap: 'wrap' }}>
          {r.cleaningPriority && r.cleaningPriority !== 'Normal' && (
            <Chip size='small' label={r.cleaningPriority} sx={{ height: 17, fontSize: 9.5, fontWeight: 700, bgcolor: `${PRIORITY_COLOR[r.cleaningPriority] || T.dim}18`, color: PRIORITY_COLOR[r.cleaningPriority] || T.dim }} />
          )}
          {r.hasMaintenance && <Chip size='small' label='Maint.' sx={{ height: 17, fontSize: 9.5, fontWeight: 700, bgcolor: '#DC262618', color: '#DC2626' }} />}
        </Box>
      </Box>
    </Card>
  );
};

const StatMini = ({ label, value, color }) => (
  <Grid item xs={6}>
    <Box sx={{ p: 1.2, borderRadius: '10px', background: `${color}0D`, textAlign: 'center' }}>
      <Typography sx={{ fontSize: 22, fontWeight: 800, color }}>{value ?? 0}</Typography>
      <Typography sx={{ fontSize: 11, color: T.dim, fontWeight: 600 }}>{label}</Typography>
    </Box>
  </Grid>
);

const AnalyticCard = ({ icon, color, label, value }) => (
  <Grid item xs={6} md={3}>
    <Panel sx={{ p: 2 }}>
      <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'grid', placeItems: 'center', background: `${color}14`, mb: 1 }}>
        <MSym name={icon} size={20} color={color} fill={1} />
      </Box>
      <Typography sx={{ fontSize: 22, fontWeight: 800, color: T.navy, lineHeight: 1.1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 12, color: T.dim, fontWeight: 600 }}>{label}</Typography>
    </Panel>
  </Grid>
);

const DrawerRow = ({ label, value, color }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.6, borderBottom: `1px solid ${T.border}` }}>
    <Typography sx={{ fontSize: 12.5, color: T.dim, fontWeight: 600 }}>{label}</Typography>
    <Typography sx={{ fontSize: 12.5, color: color || T.text, fontWeight: 700, textAlign: 'right' }}>{value}</Typography>
  </Box>
);

const ActBtn = ({ icon, label, color, onClick }) => (
  <Button fullWidth variant='contained' onClick={onClick}
    startIcon={<MSym name={icon} size={18} color='#fff' fill={1} />}
    sx={{ bgcolor: color, textTransform: 'none', fontWeight: 700, boxShadow: 'none', '&:hover': { bgcolor: color, filter: 'brightness(0.92)', boxShadow: 'none' } }}>
    {label}
  </Button>
);

const ActGrid = ({ icon, label, color, onClick }) => (
  <Grid item xs={6}>
    <Button fullWidth onClick={onClick}
      startIcon={<MSym name={icon} size={17} color={color} fill={1} />}
      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 600, fontSize: 12.5, color: T.navy,
        border: `1px solid ${T.border}`, borderRadius: '10px', py: 0.9, '&:hover': { borderColor: color, background: `${color}0A` } }}>
      {label}
    </Button>
  </Grid>
);
